// Single source of truth, also read by the backend (EmailAttachments::ALLOWED_CONTENT_TYPES).
import allowedContentTypes from "features/Resources/Shared/allowedAttachmentContentTypes.json";

// Total attachment size ceiling. Matches Postmark's 10 MB message-size limit,
// enforced again server-side (EmailAttachments::MAX_TOTAL_BYTES).
export const MAX_ATTACHMENTS_TOTAL_BYTES = 10 * 1024 * 1024;

export type ResourceAttachment = {
  id: number;
  filename: string;
  byteSize: number;
  contentType: string;
  url: string;
};

/** Presentational row rendered by AttachmentsField — persisted or in-flight. */
export type AttachmentRow = {
  key: string;
  filename: string;
  byteSize: number;
  status: "uploading" | "done" | "error";
  progress: number;
  error?: string;
  /** Present once persisted — lets the row open the file in a new tab. In-flight uploads have none. */
  url?: string;
};

const ALLOWED_ATTACHMENT_CONTENT_TYPES = new Set(allowedContentTypes);

// Fallback for when the browser reports an empty MIME type (happens for some files).
const ALLOWED_ATTACHMENT_EXTENSIONS = new Set([
  "pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "rtf",
  "txt", "csv", "png", "jpg", "jpeg", "gif", "webp", "heic", "heif",
]);

export function isAllowedAttachment(file: File): boolean {
  const type = file.type.toLowerCase();
  if (type) return ALLOWED_ATTACHMENT_CONTENT_TYPES.has(type);
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return ALLOWED_ATTACHMENT_EXTENSIONS.has(ext);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
